import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import CachedImg from '../CachedImg';
import {
  firmarFotoCompleta,
  type MoodboardFoto,
} from '../../pages/v2/moodboardsApi';

/**
 * Visor a pantalla completa de las fotos de una carpeta —
 * ADR-2026-09-13-moodboards-carpetas-de-referencia.
 *
 * Propio y chico en vez de reusar el del historial: aquel vive adentro de
 * `GeneratedImagesInline` (3600 líneas) enredado con zoom, descarga, edición y
 * el estado de las generaciones, y sacarlo de ahí es un refactor que no tiene
 * nada que ver con esto.
 *
 * Abre con la MINIATURA que la grilla ya tiene dibujada y pide el original
 * recién acá (`firmarFotoCompleta`, una firma por foto mirada): así se ve algo
 * al instante y no se firman los originales de toda la carpeta al listarla.
 */
export const VisorFotos = ({
  fotos,
  indice,
  onCerrar,
  onCambiar,
}: {
  fotos: MoodboardFoto[];
  /** Índice de la foto abierta dentro de `fotos`. */
  indice: number;
  onCerrar: () => void;
  onCambiar: (indice: number) => void;
}) => {
  const foto = fotos[indice];
  const [urlCompleta, setUrlCompleta] = useState<string | null>(null);

  const anterior = useCallback(() => {
    // Circular: desde la primera, ← lleva a la última. Es lo que hace el visor
    // de Fotos y evita el callejón sin salida de una flecha que no responde.
    onCambiar((indice - 1 + fotos.length) % fotos.length);
  }, [indice, fotos.length, onCambiar]);

  const siguiente = useCallback(() => {
    onCambiar((indice + 1) % fotos.length);
  }, [indice, fotos.length, onCambiar]);

  // El original de la foto abierta. Se resetea en cada cambio para no dejar
  // colgada la anterior a resolución completa sobre la miniatura nueva.
  useEffect(() => {
    if (!foto) return;
    let cancelado = false;
    setUrlCompleta(null);
    firmarFotoCompleta(foto.path).then((url) => {
      if (!cancelado && url) setUrlCompleta(url);
    });
    return () => {
      cancelado = true;
    };
  }, [foto?.path]);

  useEffect(() => {
    const alaTecla = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCerrar();
      if (e.key === 'ArrowLeft') anterior();
      if (e.key === 'ArrowRight') siguiente();
    };
    document.addEventListener('keydown', alaTecla);
    return () => document.removeEventListener('keydown', alaTecla);
  }, [onCerrar, anterior, siguiente]);

  if (!foto) return null;

  return createPortal(
    // z-[200]: por encima de las hojas de la app (z-[100]) y de los sheets de
    // modos, que es lo que puede estar abierto detrás en esta pantalla.
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/90"
      onClick={onCerrar}
      role="dialog"
      aria-modal="true"
      aria-label="Foto de referencia"
    >
      <button
        type="button"
        onClick={onCerrar}
        aria-label="Cerrar"
        className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
      >
        <X className="h-5 w-5" />
      </button>

      {fotos.length > 1 && (
        <>
          <button
            type="button"
            onClick={(e) => {
              // Sin esto el click sube al fondo y cierra el visor en vez de pasar
              // de foto.
              e.stopPropagation();
              anterior();
            }}
            aria-label="Foto anterior"
            className="absolute left-3 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              siguiente();
            }}
            aria-label="Foto siguiente"
            className="absolute right-3 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          >
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      {/* La miniatura primero y el original encima cuando llega: el salto de
          nitidez se nota menos que un rectángulo negro con un spinner. */}
      <div
        className="relative flex max-h-[88vh] max-w-[92vw] items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <CachedImg
          key={foto.path}
          src={urlCompleta || foto.thumbUrl}
          alt=""
          lazy={false}
          className="max-h-[88vh] max-w-[92vw] rounded-lg object-contain"
        />
        {!urlCompleta && (
          <Loader2 className="absolute bottom-3 right-3 h-5 w-5 animate-spin text-white/70" />
        )}
      </div>

      {fotos.length > 1 && (
        <p className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[13px] tabular-nums text-white/60">
          {indice + 1} de {fotos.length}
        </p>
      )}
    </div>,
    document.body,
  );
};

export default VisorFotos;
